import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createCheckoutSession, getUserDetails, fetchPersonaRecordsBatch, searchFilteredListings as searchFilteredListingsApi } from '../../util/api';
import { withoutRestrictedPostings } from '../../util/postingRead';
import { userTypeOf } from '../../util/userType';
import { JOB_WORK_AUTHORIZATION_FILTER_OPTIONS } from '../../util/jobWorkAuthorizationFilter';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_LISTINGS_APPEND_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_APPEND_REQUEST';
export const SEARCH_LISTINGS_APPEND_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_APPEND_SUCCESS';
export const SEARCH_LISTINGS_APPEND_ERROR = 'app/SearchPage/SEARCH_LISTINGS_APPEND_ERROR';
export const SEARCH_LISTINGS_COMPLETE = 'app/SearchPage/SEARCH_LISTINGS_COMPLETE';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const PAY_FOR_RFP_REQUEST = 'app/SearchPage/PAY_FOR_RFP_REQUEST';
export const PAY_FOR_RFP_SUCCESS = 'app/SearchPage/PAY_FOR_RFP_SUCCESS';
export const PAY_FOR_RFP_ERROR = 'app/SearchPage/PAY_FOR_RFP_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const SET_CURRENTUSER_ADMINID = 'app/SearchPage/SET_CURRENTUSER_ADMINID';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  currentUserAdminId: null,
  // Progressive loading state
  allResultIds: [],
  progressiveLoading: false,
  progressiveLoadingError: null,
  hasAllListings: false,
  currentOffset: 0,
  payForRFPError: null,
};

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const fetchAndEmbedPersonaRecords = async (response) => {
  const listings = response.data.data;

  // Collect all listing IDs that have a valid uuid
  const listingIds = listings
    .map(listing => listing?.id?.uuid)
    .filter(Boolean);

  // Fetch all persona records in a single batch API call instead of N individual calls
  let recordsByListingId = {};
  if (listingIds.length > 0) {
    try {
      const batchResponse = await fetchPersonaRecordsBatch({
        profileListingIds: listingIds,
        fromSearchPage: true,
      });
      if (batchResponse?.success && batchResponse?.data) {
        recordsByListingId = batchResponse.data;
      }
    } catch (error) {
      // On failure, all listings will get empty personaRecord arrays (safe fallback)
    }
  }

  // Embed persona records into each listing's attributes
  const listingsWithPersonaRecords = listings.map(listing => {
    const listingId = listing?.id?.uuid;
    const personaRecords = (listingId && recordsByListingId[listingId]) || [];
    return {
      ...listing,
      attributes: {
        ...listing.attributes,
        personaRecord: personaRecords,
      },
    };
  });

  // Return response with updated listings (same shape as before)
  return {
    ...response,
    data: {
      ...response.data,
      data: listingsWithPersonaRecords,
    },
  };
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
        // Reset progressive loading state when new search starts
        allResultIds: [],
        progressiveLoading: false,
        hasAllListings: false,
        currentOffset: 0,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_LISTINGS_APPEND_REQUEST:
      return {
        ...state,
        progressiveLoading: true,
        progressiveLoadingError: null,
      };

    case SEARCH_LISTINGS_APPEND_SUCCESS:
      const newResultIds = resultIds(payload.data);
      const accumulatedResultIds = payload.isFirstBatch
        ? newResultIds
        : [...state.allResultIds, ...newResultIds];

      const receivedListings = payload.data.data?.length || 0;
      const BATCH_SIZE = 100;

      return {
        ...state,
        allResultIds: accumulatedResultIds,
        currentPageResultIds: accumulatedResultIds,
        pagination: {
          ...payload.data.meta,
          totalItems: accumulatedResultIds.length, // Update total based on accumulated results
          paginationUnsupported: true, // Force client-side pagination
        },
        progressiveLoading: false,
        hasAllListings: receivedListings < BATCH_SIZE, // If we got less than batch size, we're done
        currentOffset: state.currentOffset + receivedListings,
      };

    case SEARCH_LISTINGS_APPEND_ERROR:
      return {
        ...state,
        progressiveLoading: false,
        progressiveLoadingError: payload,
      };

    case SEARCH_LISTINGS_COMPLETE:
      return {
        ...state,
        hasAllListings: true,
        progressiveLoading: false,
      };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case SET_CURRENTUSER_ADMINID:
      return {
        ...state,
        currentUserAdminId: payload,
      };

    case PAY_FOR_RFP_REQUEST:
      return {
        ...state,
        payForRFPError: null,
      };

    case PAY_FOR_RFP_SUCCESS:
      const { url } = payload;
      window.location.href = url;

      return {
        ...state,
        payForRFPError: null,
      };

    case PAY_FOR_RFP_ERROR:
      return {
        ...state,
        payForRFPError: payload,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchListingsAppendRequest = () => ({
  type: SEARCH_LISTINGS_APPEND_REQUEST,
});

export const searchListingsAppendSuccess = (response, isFirstBatch = false) => ({
  type: SEARCH_LISTINGS_APPEND_SUCCESS,
  payload: { data: response.data, isFirstBatch },
});

export const searchListingsAppendError = e => ({
  type: SEARCH_LISTINGS_APPEND_ERROR,
  error: true,
  payload: e,
});

export const searchListingsComplete = () => ({
  type: SEARCH_LISTINGS_COMPLETE,
});

export const setCurrentUserAdminId = payload => ({
  type: SET_CURRENTUSER_ADMINID,
  payload: payload,
});

export const payForRFPRequest = () => ({
  type: PAY_FOR_RFP_REQUEST,
});

export const payForRFPSuccess = (response) => ({
  type: PAY_FOR_RFP_SUCCESS,
  payload: response,
});

export const payForRFPError = e => ({
  type: PAY_FOR_RFP_ERROR,
  error: true,
  payload: e,
});

// ================ Server-side filter translation for job/rfp listings ================ //
// Maps URL filter param keys to actual publicData field names and stored label values.
// This allows Sharetribe's API to filter server-side instead of loading all listings client-side.
// IMPORTANT: Requires corresponding search schemas to be configured via Flex CLI for each field.

export const JOB_LISTING_FILTER_MAP = {
  pub_work_site_preference: {
    apiField: 'pub_workSiteTypeJob',
    valueMap: { remote: 'Remote', on_site: 'On-site', hybrid: 'Hybrid' },
  },
  pub_work_engagement_type: {
    apiField: 'pub_workEngagementTypeJob',
    valueMap: {
      w2c_full_time: 'W2 Full Time',
      w2_contract: 'W2 Contract',
      c2c: 'Corp-to-Corp (C2C)',
      c2h: 'C2H',
      '1099': '1099',
      independent_contractor: 'Independent Contractor',
      intern: 'Intern',
    },
  },
  pub_employment_type: {
    apiField: 'pub_employmentTypeJob',
    valueMap: {
      'full time': 'Full Time',
      'part time': 'Part Time',
      contract: 'Contract',
      temporary: 'Temporary',
      internship: 'Internship',
      freelance: 'Freelance',
    },
  },
  pub_experience_level: {
    apiField: 'pub_experienceLevelJob',
    valueMap: {
      entry_level: 'Entry Level',
      mid_level: 'Mid Level',
      senior_level: 'Senior Level',
      lead_principal: 'Lead/Principal',
      executive: 'Executive',
    },
  },
  pub_work_authorization: {
    apiField: 'pub_workAuthReqJob',
    valueMap: Object.fromEntries(JOB_WORK_AUTHORIZATION_FILTER_OPTIONS.map(({ option, label }) => [option, label])),
  },
  pub_certifications_required: {
    apiField: 'pub_certificationsRequiredJob',
    valueMap: { yes: 'Yes', no: 'No' },
  },
  pub_travel_requirement: {
    apiField: 'pub_isTravelRequiredJob',
    valueMap: { yes: 'Yes', no: 'No' },
  },
};

const RFP_LISTING_FILTER_MAP = {
  pub_category_codes: {
    apiField: 'pub_categoryRFP',
    valueMap: { NAICS: 'NAICS', 'PSC/FSC': 'PSC/FSC', NIGP: 'NIGP', UNSPSC: 'UNSPSC' },
  },
  pub_organization_type: {
    apiField: 'pub_issuingOrgTypeRFP',
    valueMap: {
      federal: 'Federal',
      state: 'State',
      local: 'Local',
      private: 'Private',
      nonprofit: 'Nonprofit',
    },
  },
  pub_delivery_model: {
    apiField: 'pub_deliveryModelsRFP',
    valueMap: { onsite: 'Onsite', offsite: 'Offsite', offshore: 'Offshore', hybrid: 'Hybrid' },
  },
  pub_security_certifications: {
    apiField: 'pub_securityCertificationsRFP',
    valueMap: {
      soc2: 'SOC2',
      iso27001: 'ISO27001',
      fedramp: 'FedRAMP',
      pci: 'PCI',
      iso9001: 'ISO 9001',
      cmmi_level3: 'CMMI Level 3+',
    },
  },
  pub_diversity_certifications: {
    apiField: 'pub_diversityCertificationsRFP',
    valueMap: {
      minority_owned: 'Minority-Owned',
      woman_owned: 'Woman-Owned',
      veteran_owned: 'Veteran-Owned',
      'sba_8(a)': 'SBA 8(a)',
      nmsdc: 'NMSDC',
    },
  },
  pub_data_protection_controls: {
    apiField: 'pub_dataProtectionControlsRFP',
    valueMap: {
      gdpr: 'GDPR',
      hipaa: 'HIPAA',
      ccpa: 'CCPA',
      sox: 'SOX',
    },
  },
};

// ================ Hybrid mode detection for job/rfp ================ //
// URL-based filters that cannot be handled server-side (nested data, multi-field ranges, etc.)
// When any of these are present, we fall back to hybrid mode: fetch perPage=100 with client-side pagination.
const JOB_CLIENT_SIDE_ONLY_URL_FILTERS = ['keywords', 'pub_required_ver_job'];
const RFP_CLIENT_SIDE_ONLY_URL_FILTERS = [
  'keywords', 'technology',
  'pub_service_domain', 'pub_technology_domain',
  'pub_kyb_status_verification', 'pub_beneficial_ownership_verification',
  'pub_past_performance_evidence',
];

const hasClientSideOnlyFilters = (searchParams, listingType) => {
  const clientFilters = listingType === 'job_listing'
    ? JOB_CLIENT_SIDE_ONLY_URL_FILTERS
    : listingType === 'rfp_listing'
    ? RFP_CLIENT_SIDE_ONLY_URL_FILTERS
    : [];
  return clientFilters.some(key => {
    const val = searchParams[key];
    return val !== undefined && val !== null && val !== '';
  });
};

/**
 * Translates URL filter params to Sharetribe API query params.
 * Maps filter option keys to actual stored publicData label values.
 * Only translates filters that check top-level (non-nested) publicData.
 */
const getTranslatedApiFilters = (params, listingType) => {
  const filterMap = listingType === 'job_listing'
    ? JOB_LISTING_FILTER_MAP
    : listingType === 'rfp_listing'
    ? RFP_LISTING_FILTER_MAP
    : null;
  if (!filterMap) return {};

  const translated = {};
  Object.entries(params).forEach(([key, value]) => {
    if (!value || !filterMap[key]) return;

    const { apiField, valueMap } = filterMap[key];
    const rawValue = String(value);

    // Extract search mode prefix (has_any: or has_all:) if present
    const prefixMatch = rawValue.match(/^(has_any:|has_all:)/);
    const prefix = prefixMatch ? prefixMatch[1] : '';
    const valueWithoutPrefix = prefix
      ? rawValue.slice(prefix.length)
      : rawValue;

    // Split by comma, translate each value to stored label, and rejoin
    const translatedValues = valueWithoutPrefix.split(',').map(v => {
      const trimmed = v.trim();
      return valueMap[trimmed] || valueMap[trimmed.toLowerCase()] || trimmed;
    });

    // Use has_all (AND) by default when multiple values are selected and no prefix was specified.
    // This ensures filters use AND logic — a listing must match ALL selected values.
    const effectivePrefix = prefix || (translatedValues.length > 1 ? 'has_all:' : '');
    translated[apiField] = effectivePrefix + translatedValues.join(',');
  });

  return translated;
};

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue =
        typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    page,
    pub_listingType,
    ...restOfParams
  } = searchParams;

  // Check if any custom filters are being used (these are the pub_ parameters that we filter out later)
  const hasCustomFilters = Object.keys(restOfParams).some(key => key.startsWith('pub_') && key !== 'pub_listingType');
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);

  // Determine listing type and pagination mode for job/rfp
  const effectiveListingType = pub_listingType || listingTypePathParam;
  const isJobOrRfpListing = effectiveListingType === 'job_listing' || effectiveListingType === 'rfp_listing';
  const useHybridMode = isJobOrRfpListing && hasClientSideOnlyFilters(searchParams, effectiveListingType);
  const useServerPagination = isJobOrRfpListing && !useHybridMode;

  // For server-side pagination mode, map sort to native createdAt.
  // The client uses 'createdAt' for newest-first, but the SDK uses '-createdAt' for descending.
  // For hybrid/other modes, keep original sort behavior (client-side sort handles it).
  const sortMaybe = (() => {
    if (sort === config.search.sortConfig.relevanceKey) return {};
    if (useServerPagination && sort) {
      if (sort === 'createdAt') return { sort: '-createdAt' }; // newest first
      if (sort === '-createdAt') return { sort: 'createdAt' }; // oldest first
      return { sort };
    }
    if (useServerPagination) {
      // Default sort for job/rfp server pagination: newest first
      return { sort: '-createdAt' };
    }
    return sort ? { sort } : {};
  })();

  let params = {
    ...omitInvalidCategoryParams(restOfParams),
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...(pub_listingType ? { pub_listingType } : {}),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    // Server-side pagination for job/rfp: use RESULT_PAGE_SIZE and pass page number.
    // Hybrid mode (client-side-only filters active): fetch 100 for a larger filter pool.
    // Other listing types: fetch 100 (existing behavior).
    perPage: useServerPagination ? RESULT_PAGE_SIZE : 100,
    ...(useServerPagination ? { page: page || 1 } : {}),
  };

  // Filter out all pub_ parameters EXCEPT pub_listingType which we need for server-side filtering
  params = Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !key.startsWith("pub_") || key === "pub_listingType"
    )
  );

  // For job_listing and rfp_listing, add translated top-level publicData filters
  // so the Sharetribe API handles filtering server-side (avoids loading all listings client-side).
  // Filters for nested data (skills, onBoardingInfo, etc.) remain client-side only.
  if (isJobOrRfpListing) {
    const translatedApiFilters = getTranslatedApiFilters(restOfParams, effectiveListingType);
    params = { ...params, ...translatedApiFilters };
  }

  const cleanedFieldsListing = params['fields.listing']
    ? [
        // Filter out all "publicData.*" entries
        ...params['fields.listing'].filter(f => !f.startsWith('publicData.')),
        // Add "publicData" once at the end
        'publicData',
      ]
    : ['publicData']; // fallback if none exist

  params = {
    ...params,
    'fields.listing': cleanedFieldsListing,
  };

  if(params?.keywords) {
    // For candidate and job listings, don't use keywords in API query
    // Client-side filtering will handle keyword search on candidateHeading and requiredSkillsJob
    const isCandidateListing = params?.pub_listingType === 'candidate';
    const isJobListing = params?.pub_listingType === 'job_listing';

    if (isCandidateListing || isJobListing) {
      // Remove keywords from params for API query but keep in URL for client-side filtering
      const paramsWithoutKeywords = {...params};
      delete paramsWithoutKeywords.keywords;

      return sdk.listings
        .query(paramsWithoutKeywords)
        .then(async response => {
          const listingFields = config?.listing?.listingFields;
          const sanitizeConfig = { listingFields };

          // Always indicate client-side pagination for custom filters
          if (response.data.meta) {
            response.data.meta.paginationUnsupported = true;
          }

          // Fetch and embed persona records for each listing
          const responseWithPersonaRecords = await fetchAndEmbedPersonaRecords(withoutRestrictedPostings(response));

          dispatch(addMarketplaceEntities(responseWithPersonaRecords, sanitizeConfig));
          dispatch(searchListingsSuccess(responseWithPersonaRecords));
          return responseWithPersonaRecords;
        })
        .catch(e => {
          const error = storableError(e);
          dispatch(searchListingsError(error));
          if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
            
          }
        });
    }

    const tagsParams = {...params};
    tagsParams.pub_tags = params.keywords;
    delete tagsParams.keywords;


    return Promise.all([
      sdk.listings.query(params),
      sdk.listings.query(tagsParams),
    ])
    .then(async ([keywordRes, tagRes]) => {
      const allListings = [...(keywordRes.data.data || []), ...(tagRes.data.data || [])];

      const uniqueListings = Array.from(
        new Map(
          allListings
            .filter(l => l && l.id && l.id.uuid)
            .map(l => [l.id.uuid, l])
        ).values()
      );

      const totalCount = uniqueListings.length;

      const combinedResponse = {
        data: {
          data: uniqueListings,
          included: [...(keywordRes.data.included || []), ...(tagRes.data.included || [])],
           meta: {
            ...keywordRes.data.meta,
            totalItems: totalCount,
            // Always use client-side pagination for custom filters
            paginationUnsupported: true,
          },
        },
        status: keywordRes.status || 200,
        statusText: '',
      };
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      // Fetch and embed persona records for each listing
      const responseWithPersonaRecords = await fetchAndEmbedPersonaRecords(withoutRestrictedPostings(combinedResponse));

      dispatch(addMarketplaceEntities(responseWithPersonaRecords, sanitizeConfig));
      dispatch(searchListingsSuccess(responseWithPersonaRecords));
      return responseWithPersonaRecords;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        
      }
    });
  }

  return sdk.listings
    .query(params)
    .then(async response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      // For job/rfp server-side pagination, let the API's meta flow through unchanged.
      // For hybrid mode or other listing types, use client-side pagination.
      if (!useServerPagination && response.data.meta) {
        response.data.meta.paginationUnsupported = true;
      }

      // Fetch and embed persona records for each listing
      const responseWithPersonaRecords = await fetchAndEmbedPersonaRecords(withoutRestrictedPostings(response));

      dispatch(addMarketplaceEntities(responseWithPersonaRecords, sanitizeConfig));
      dispatch(searchListingsSuccess(responseWithPersonaRecords));
      return responseWithPersonaRecords;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {

      }
    });
};

export const loadListingsProgressively = (searchParams, config) => async (dispatch, getState, sdk) => {
  const BATCH_SIZE = 100; // Sharetribe default limit
  const MAX_BATCHES = 50000; // Maximum 5,000,000 listings to handle very large datasets
  let currentPage = 1;
  let hasMore = true;
  let batchCount = 0;

  // Extract listing type from searchParams
  const listingType = searchParams?.pub_listingType;

  while (hasMore && batchCount < MAX_BATCHES) {
    try {
      dispatch(searchListingsAppendRequest());

      // Build base parameters for this batch with listing type filter
      const batchParams = {
        ...searchParams,
        page: currentPage,
        perPage: BATCH_SIZE,
        // Always include the listing type to filter at server level
        ...(listingType ? { pub_listingType: listingType } : {}),
      };

      // Remove ALL custom filters from API call since we'll handle them client-side
      // BUT keep pub_listingType to fetch only the relevant listing type
      const filteredBatchParams = Object.fromEntries(
        Object.entries(batchParams).filter(
          ([key]) => !key.startsWith("pub_") || key === "pub_listingType"
        )
      );

      // Make API call with proper error handling
      const response = await sdk.listings.query({
        ...filteredBatchParams,
        'fields.listing': ['publicData', 'title', 'state', 'deleted', 'geolocation', 'price'],
        'fields.user': ['profile.displayName'],
        'fields.image': ['variants.scaled-small'],
        'limit.images': 1,
      });

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      // Fetch and embed persona records for each listing
      const responseWithPersonaRecords = await fetchAndEmbedPersonaRecords(withoutRestrictedPostings(response));

      // Add entities to Redux store
      dispatch(addMarketplaceEntities(responseWithPersonaRecords, sanitizeConfig));

      // Mark this batch as successfully loaded
      dispatch(searchListingsAppendSuccess(responseWithPersonaRecords, currentPage === 1));

      const receivedListings = response.data.data?.length || 0;

      // Determine if we should continue loading
      hasMore = receivedListings === BATCH_SIZE && currentPage < MAX_BATCHES;

      // Safety check: if we received less than requested, we're done
      if (receivedListings < BATCH_SIZE) {
        hasMore = false;
      }

      currentPage++;
      batchCount++;

      // Add a small delay between batches to avoid overwhelming the server
      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }

    } catch (error) {
      dispatch(searchListingsAppendError(storableError(error)));
      hasMore = false;
    }
  }

  dispatch(searchListingsComplete());
};

/**
 * searchWithFilters — calls the backend API endpoint to fetch, filter, and paginate listings server-side.
 * Only filtered listings are returned, so nothing extra is visible in the network tab.
 *
 * @param {Object} searchParams - URL-based search params (pub_listingType, keywords, sort, page, etc.)
 * @param {Object} filterParams - All filter values (URL-parsed + state-based) to apply server-side
 * @param {Object} config - App config (used for listing fields)
 */
export const searchWithFilters = (searchParams, filterParams = {}, config) => async (dispatch, getState) => {
  dispatch(searchListingsRequest(searchParams));

  try {
    const page = searchParams.page || filterParams.page || 1;
    const perPage = searchParams.perPage || RESULT_PAGE_SIZE;

    const response = await searchFilteredListingsApi({
      searchParams: {
        pub_listingType: searchParams.pub_listingType || searchParams.listingTypePathParam,
        keywords: searchParams.keywords,
        include: ['author', 'images'],
        sort: searchParams.sort || filterParams.sortByFilter,
        bounds: searchParams.bounds,
      },
      filterParams: {
        ...filterParams,
        selectedListingType: searchParams.pub_listingType || searchParams.listingTypePathParam || filterParams.selectedListingType,
        keywordsFromURL: searchParams.keywords,
      },
      page,
      perPage,
    });

    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    // The response from the backend matches Sharetribe SDK shape
    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(searchListingsSuccess(response));
    return response;
  } catch (e) {
    const error = storableError(e);
    dispatch(searchListingsError(error));
    if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
      // Silent error handling (same as existing code)
    }
  }
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const currentUserProfileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId || currentUser?.attributes?.profile?.publicData?.firstListingId;
  if (currentUserProfileListingId) {
    try {
      let currentUserProfileListing = await sdk.listings.show({ id: currentUserProfileListingId });
      currentUserProfileListing = currentUserProfileListing?.data?.data;
      
      dispatch(setCurrentUserAdminId(currentUserProfileListing?.attributes?.publicData?.adminId));
    } catch (er) {
      // Silently handle - admin ID fetch can fail for some user types
    }
  }



  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  let searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [
        'variants.square-small',
        'variants.square-small2x',
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );

  if(queryParams?.pub_listingType === undefined && (queryParams?.address === undefined || queryParams?.address === '')) {
    let defaultListingType = 'candidate';
    if (userTypeOf(currentUser) === 'candidate') {
      defaultListingType = 'job_listing';
    }

    searchListingsCall = searchListings(
      {
        ...rest,
        ...originMaybe,
        ...listingTypeVariantMaybe,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['author', 'author.profileImage', 'images'],
        pub_listingType: defaultListingType,
        'fields.listing': [
          'title',
          'geolocation',
          'price',
          'deleted',
          'state',
          'publicData.listingType',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          // These help rendering of 'purchase' listings,
          // when transitioning from search page to listing page
          'publicData.pickupEnabled',
          'publicData.shippingEnabled',
          'publicData.priceVariationsEnabled',
          'publicData.priceVariants',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
        'fields.image': [
          'variants.square-small',
          'variants.square-small2x',
          'variants.scaled-small',
          'variants.scaled-medium',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      },
      config
    );
  }
  else if (queryParams?.pub_listingType) {
    searchListingsCall = searchListings(
      {
        ...rest,
        ...originMaybe,
        ...listingTypeVariantMaybe,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['author', 'author.profileImage', 'images'],
        pub_listingType: queryParams?.pub_listingType,
        'fields.listing': [
          'title',
          'geolocation',
          'price',
          'deleted',
          'state',
          'publicData.listingType',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          // These help rendering of 'purchase' listings,
          // when transitioning from search page to listing page
          'publicData.pickupEnabled',
          'publicData.shippingEnabled',
          'publicData.priceVariationsEnabled',
          'publicData.priceVariants',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
        'fields.image': [
          'variants.square-small',
          'variants.square-small2x',
          'variants.scaled-small',
          'variants.scaled-medium',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      },
      config
    );
  }

  // In browser context, skip the Marketplace SDK call. The component's useEffect
  // will call onSearchWithFilters (backend API) which returns only filtered/paginated
  // listings — so the network tab never exposes all listings.
  if (typeof window !== 'undefined') {
    return Promise.resolve();
  }

  return dispatch(searchListingsCall);
};

export const payForRFPDuck = (priceId, quantity, type, rfpListingId) => async (dispatch, getState, sdk) => {
  dispatch(payForRFPRequest());

  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileListing = state.user?.profileListing;
  
  const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId ||
                    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let email = null;
  let adminId = '';

  if (!profileListingId) {
    return;
  }

  if (userTypeOf(currentUser) === 'candidate') {
    adminId = currentUser?.id?.uuid;
    email = currentUser?.attributes?.email;
  } else {
    // let profileListing = await sdk.listings.show({ id: profileListingId });
    // profileListing = profileListing?.data?.data;

    email = currentUser?.attributes?.email;

    adminId = profileListing?.attributes?.publicData?.adminId;
    // if (adminId) {
    //   const adminUserResponse = await getUserDetails({ userId: adminId });
    //   const adminUser = adminUserResponse?.data?.data;
    //   email = adminUser?.attributes?.email;
    // }
  }

  if (!email) {
    return;
  }

  return createCheckoutSession({ priceId, quantity, type, email, paidBy: adminId, isRFPApplicationCard: true, rfpListingId: rfpListingId })
    .then(async response => {
      
      dispatch(payForRFPSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(payForRFPError(storableError(e)));
      throw e;
    });
};
